import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { FamilyService } from '../../services/family.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { FamilyStudent } from '../../models/family-student.model';
import { Injectable } from '@angular/core';
import { UpdateFamilyData } from '../../models/family-data.model';
import { FamilyActions } from './family.actions';
import { FamilyForm } from '../../models/family-form.model';
import { Family } from '../../models/family-response.model';
import { StudentData, UpdateStudentData } from 'src/app/modules/student/models/student-data.model';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { StudentScholarshipData, UpdateStudentScholarshipData } from 'src/app/modules/scholarship/models/student-scholarship-data.model';
import { StudentEngagementData, UpdateStudentEngagementData } from 'src/app/modules/engagement/models/engagement-student.model';
import { FamilyRequest } from '../../models/family-request.model';
import { FamilyAddForm } from '../../models/family-form-add.model';
import { FamilyFilter } from '../../models/family-filter.model';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';
import { ToastrService } from 'ngx-toastr';
import { FormArray, FormGroup } from '@angular/forms';
import { FamilyValidateDupDNIAccount } from '../../models/family-response-validateduplicated-dniaccount.model';
import { AuthSelectors } from 'src/app/modules/login/store/auth.selectors';

export class FamilyStateModel {
  families: Family[];
  familyStudent: FamilyStudent;
  familyForm: {
    model: FamilyForm;
  };
  familyAddForm: {
    model: FamilyAddForm;
  };
  filters: {
    model: FamilyFilter;
  };
  public isDuplicatedDNIBank: FamilyValidateDupDNIAccount;
  public isLoadingFamilies: boolean;
  public isListSmaller: boolean;
  public isSubmitted: boolean;
  public isEdited: boolean;
  public error: string;
  public exerciseId: number;
}

const defaults = {
  families: null,
  familyStudent: null,
  familyForm: {
    model: null,
  },
  familyAddForm: {
    model: null,
  },
  filters: {
    model: null,
  },
  isDuplicatedDNIBank: null,
  isLoadingFamilies: false,
  isSubmitted: false,
  isListSmaller: true,
  isEdited: false,
  error: null,
  exerciseId: null,
};

@State<FamilyStateModel>({
  name: 'families',
  defaults,
})
@Injectable()
export class FamilyState {
  constructor(
    private familyService: FamilyService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store,
    private toastr: ToastrService
  ) {}

  @Action(FamilyActions.GetFamilies) getFamilies(
    ctx: StateContext<FamilyStateModel>,
    {}: FamilyActions.GetFamilies
  ) {
    let state = ctx.getState();
    let filters = state.filters.model;
    if (
      (filters.schoolId && filters.schoolId > 0) ||
      (filters.identification && filters.identification.length > 0) ||
      (filters.customerNumber && filters.customerNumber.length > 0)
    ) {
      this.callGetFamilies(ctx, filters);
    } else {
      this.alertService
        .confirm(
          this.getTitle(),
          this.localizationService.translate(
            'families.highNumberRecordsConfirm'
          ),
          this.localizationService.translate('buttons.accept'),
          this.localizationService.translate('buttons.cancel'),
          this.localizationService.translate(
            'families.highNumberRecordsConfirm'
          )
        )
        .then((result) => {
          if (result.value) {
            this.callGetFamilies(ctx, filters);
          }
        });
    }
  }

  @Action(FamilyActions.GetFamiliesSuccess)
  getFamiliesSuccess(
    ctx: StateContext<FamilyStateModel>,
    { families }: FamilyActions.GetFamiliesSuccess
  ) {
    ctx.patchState({ families: families, isLoadingFamilies: false });
  }

  @Action(FamilyActions.GetIsDuplicatedDNIBank)
  GetIsDuplicatedDNIBank(ctx: StateContext<FamilyStateModel>,
    { duplicatedDNIBankAccount }: FamilyActions.GetIsDuplicatedDNIBank) {
      return this.familyService.getIsDuplicatedDNIBank(duplicatedDNIBankAccount)
      .subscribe({
        next: (response) =>
          ctx.dispatch(
            new FamilyActions.GetIsDuplicatedDNIBankSuccess(response)
          ),
        error: (error) => ctx.dispatch(new FamilyActions.Error(error)),
      });
  }

  @Action(FamilyActions.GetIsDuplicatedDNIBankSuccess)
  getIsDuplicatedDNIBankSuccess(
    ctx: StateContext<FamilyStateModel>,
    { isDuplicated }: FamilyActions.GetIsDuplicatedDNIBankSuccess
  ): void {
    ctx.patchState({ isDuplicatedDNIBank: isDuplicated });
  }
 
  @Action(FamilyActions.RefreshFamilies)
  refreshFamilies(
    ctx: StateContext<FamilyStateModel>,
    {}: FamilyActions.RefreshFamilies
  ) {
    let state = ctx.getState();
    let families = state.families;
    let filters = state.filters.model;
    if (families && families.length > 0) {
      this.callGetFamilies(ctx, filters);
    }
  }

  @Action(FamilyActions.GetFamilyStudent) getFamilyStudent(
    ctx: StateContext<FamilyStateModel>,
    { familyId, exerciseId }: FamilyActions.GetFamilyStudent
  ) {
    ctx.patchState({ isLoadingFamilies: true });
    return this.familyService
      .getFamilyStudentById(familyId, exerciseId)
      .subscribe({
        next: (response) =>
          ctx.dispatch(
            new FamilyActions.GetFamilyStudentSuccess(response, exerciseId)
          ),
        error: (error) => ctx.dispatch(new FamilyActions.Error(error)),
      });
  }

  @Action(FamilyActions.GetFamilyStudentSuccess)
  getFamilyStudentSuccess(
    ctx: StateContext<FamilyStateModel>,
    { familyStudent, exerciseId }: FamilyActions.GetFamilyStudentSuccess
  ) {
    let familyForm = this.getFamilyForm(familyStudent);

    var state = ctx.getState();
    ctx.patchState({
      ...state,
      isLoadingFamilies: false,
      familyStudent: familyStudent,
      familyForm: { model: familyForm },
      exerciseId: exerciseId,
    });
  }

  @Action(FamilyActions.AddStudentToFamily)
  addStudentToFamily(
    ctx: StateContext<FamilyStateModel>,
    { studentData }: FamilyActions.AddStudentToFamily
  ) {
    ctx.setState(
      patch({
        familyStudent: patch({
          familyStudents: append<StudentData>([studentData]),
        }),
      })
    );
  }

  @Action(FamilyActions.DeleteStudentOnFamily)
  deleteStudentOnFamily(
    ctx: StateContext<FamilyStateModel>,
    { studentId }: FamilyActions.DeleteStudentOnFamily
  ) {
    ctx.setState(
      patch({
        familyStudent: patch({
          familyStudents: removeItem<StudentData>((student) => student.studentId === studentId)
        }),
      })
    );
  }

  @Action(FamilyActions.SetFilterCurrentExercise)
  setFilterCurrentExercise(
    ctx: StateContext<FamilyStateModel>,
    { exerciseId }: FamilyActions.SetFilterCurrentExercise
  ) {
    ctx.setState(
      patch({
        filters: patch({
          model: patch({
            exerciseId: exerciseId,
          }),
        }),
      })
    );
  }

  @Action(FamilyActions.SetIsListSmaller)
  setIsListSmaller(
    ctx: StateContext<FamilyStateModel>,
    { isListSmaller }: FamilyActions.SetIsListSmaller
  ) {
    ctx.patchState({
      isListSmaller: isListSmaller,
    });
  }

  @Action(FamilyActions.SetIsEdited)
  setIsEdited(
    ctx: StateContext<FamilyStateModel>,
    { isEdited }: FamilyActions.SetIsEdited
  ) {
    ctx.patchState({
      isEdited,
    });
  }

  @Action(FamilyActions.AddStudentScholarship)
  addStudentScholarship(
    ctx: StateContext<FamilyStateModel>,
    { studentScholarshipData }: FamilyActions.AddStudentScholarship
  ) {
    ctx.setState(
      patch({
        familyStudent: patch({
          familyStudents: updateItem<StudentData>(
            (student) => student.studentId === studentScholarshipData.studentId,
            patch({
              currentScholarshipFamilyStudents: append<StudentScholarshipData>([
                studentScholarshipData,
              ]),
            })
          ),
        }),
      })
    );
  }

  @Action(FamilyActions.DeleteStudentScholarship)
  deleteStudentScholarship(
    ctx: StateContext<FamilyStateModel>,
    { studentId, studentScholarshipId }: FamilyActions.DeleteStudentScholarship
  ) {
    ctx.setState(
      patch({
        familyStudent: patch({
          familyStudents: updateItem<StudentData>(
            (student) => student.studentId === studentId,
            patch({
              currentScholarshipFamilyStudents: removeItem<StudentScholarshipData>(
                (studentScholarship) =>
                  studentScholarship.studentScholarshipId ===
                  studentScholarshipId
              ),
            })
          ),
        }),
      })
    );
  }

  @Action(FamilyActions.AddStudentEngagement)
  addStudentEngagement(ctx: StateContext<FamilyStateModel>, { studentEngagementData }: FamilyActions.AddStudentEngagement) {
    ctx.setState(
      patch({
        familyStudent: patch({
          familyStudents: updateItem<StudentData>(
            (student) => student.studentId === studentEngagementData.studentId,
            patch({
              currentEngagementFamilyStudents: append<StudentEngagementData>([
                studentEngagementData,
              ]),
            })
          ),
        }),
      })
    );
  }

  @Action(FamilyActions.DeleteStudentEngagement)
  deleteStudentEngagement(ctx: StateContext<FamilyStateModel>, { engagementId, studentId }: FamilyActions.DeleteStudentEngagement) {
    ctx.setState(
      patch({
        familyStudent: patch({
          familyStudents: updateItem<StudentData>(
            (student) => student.studentId == studentId,
            patch({
              currentEngagementFamilyStudents: removeItem<StudentEngagementData>((engagement) => engagement.engagementId == engagementId),
            })
          ),
        }),
      })
    );
  }

  @Action(FamilyActions.EditFamily)
  editFamily(ctx: StateContext<FamilyStateModel>, { user, familyDetailForm }: FamilyActions.EditFamily) {
    var state = ctx.getState();
    let textConfirmation = 'families.familyEditConfirm';
    let textDNIDuplicated = this.localizationService.translate('families.familyEditConfirmWithDuplicatedDNI');
    let textBankDuplicated = this.localizationService.translate('families.familyEditConfirmWithDuplicatedBankAccount');
    let updateFamilyData = this.createUpdateFamilyData(familyDetailForm, state);
    this.alertService
      .confirm(
        this.localizationService.translate(this.getTitle()),
        this.localizationService.translate(textConfirmation),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService
        .translate(textConfirmation)
        .replace('{0}', updateFamilyData.familyName + ' ' + updateFamilyData.firstName)
        .replace('{1}', state.isDuplicatedDNIBank.isDuplicatedDNI? textDNIDuplicated : '')
        .replace('{2}', state.isDuplicatedDNIBank.isDuplicatedBankAccount? textBankDuplicated : '')
    )
      .then((result) => {
        if (result.value) {
          ctx.patchState({ isLoadingFamilies: true });
          return this.familyService
            .updateFamily(updateFamilyData, user)
            .subscribe({
              next: () => ctx.dispatch(new FamilyActions.EditFamilySuccess(updateFamilyData)),
              error: (error) => ctx.dispatch(new FamilyActions.Error(error)),
            });
        } else {
          return null;
        }
      });
  }

  @Action(FamilyActions.EditFamilySuccess)
  editFamilySuccess(
    ctx: StateContext<FamilyStateModel>,
    {updateFamilyData}: FamilyActions.EditFamilySuccess
  ) {
    var state = ctx.getState();
    ctx.patchState({
      ...state,
      isLoadingFamilies: false,
    });
    ctx.setState(
      patch({
        familyStudent: patch({
          family: patch({
            familyName: updateFamilyData.familyName,
            firstName: updateFamilyData.firstName,
            lastName: updateFamilyData.lastName,
          })
        }),
      })
    );
  }

  @Action(FamilyActions.DeleteFamily)
  deleteFamily(ctx: StateContext<FamilyStateModel>, { familyId, user }: FamilyActions.DeleteFamily) {
    this.alertService.confirm(
      this.localizationService.translate(this.getTitle()),
      this.localizationService.translate('families.familyDeleteConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('families.familyDeleteConfirm')
    ).then((result) => {
      if (result.value) {
        return this.familyService.deleteFamily(familyId, user).subscribe({
          next: () => ctx.dispatch(new FamilyActions.DeleteFamilySuccess()),
          error: (error) => ctx.dispatch(new FamilyActions.Error(error)),
        });
      } else {
        return null;
      }
    });
  }

  @Action(FamilyActions.DeleteFamilySuccess)
  deleteFamilySuccess(ctx: StateContext<FamilyStateModel>, { }: FamilyActions.DeleteFamilySuccess) {
    var state = ctx.getState();
    ctx.patchState({
      ...state,
      isLoadingFamilies: false,
    });
    this.showToastr(this.localizationService.translate('families.familyDeleteSuccessful'));
  }

  @Action(FamilyActions.SetStudentDiet)
  setStudentDiet(
    ctx: StateContext<FamilyStateModel>,
    { dietId, studentId, value }: FamilyActions.SetStudentDiet
  ) {
    let state = ctx.getState();
    let newStudentDiets = state.familyForm.model.familyStudents.find(
      (student) => student.studentId === studentId
    ).studentDiets;
    if (!newStudentDiets) newStudentDiets = [];
    if (value) {
      newStudentDiets = [...newStudentDiets, dietId];
    } else {
      newStudentDiets = newStudentDiets.filter((diet) => diet !== dietId);
    }

    ctx.setState(
      patch({
        familyForm: patch({
          model: patch({
            familyStudents: updateItem<StudentData>(
              (student) => student.studentId === studentId,
              patch({
                studentDiets: newStudentDiets
              })
            ),
          }),
        }),
      })
    );
  }

  @Action(FamilyActions.SetIsSubmitted)
  setIsSubmitted(ctx: StateContext<FamilyStateModel>, { isSubmitted }: FamilyActions.SetIsSubmitted
  ) {
    ctx.patchState({
      isSubmitted: isSubmitted,
    });
  }

  @Action(FamilyActions.AddFamily)
  addFamily( ctx: StateContext<FamilyStateModel>, { family, user }: FamilyActions.AddFamily ) 
  {
    var state = ctx.getState();
    let textConfirmation = 'families.familyAddConfirm';
    let textDNIDuplicated = this.localizationService.translate('families.familyEditConfirmWithDuplicatedDNI');
    let textBankDuplicated = this.localizationService.translate('families.familyEditConfirmWithDuplicatedBankAccount');
    this.alertService
      .confirm(
        this.localizationService.translate('labels.family'),
        this.localizationService.translate(textConfirmation),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService
        .translate(textConfirmation)
        .replace('{0}', family.familyName)
        .replace('{1}', state.isDuplicatedDNIBank.isDuplicatedDNI? textDNIDuplicated : '')
        .replace('{2}', state.isDuplicatedDNIBank.isDuplicatedBankAccount? textBankDuplicated : '')
   )
      .then((result) => {
        if (result.value) {
          let familyRequest = this.getFamilyRequest(family);
          return this.familyService
            .createFamily(familyRequest, user)
            .subscribe({
              next: (newFamilyId) => {
                ctx.dispatch(new FamilyActions.AddFamilySuccess(newFamilyId));
              },
              error: (error) => {
                ctx.dispatch(new FamilyActions.Error(error));
              },
            });
        } else {
          return null;
        }
      });
  }

  @Action(FamilyActions.AddFamilySuccess)
  addFamilySuccess(
    ctx: StateContext<FamilyStateModel>,
    { newFamilyId }: FamilyActions.AddFamilySuccess
  ) {
    const state = ctx.getState();
    ctx.patchState({
      isLoadingFamilies: false,
    });

    this.showToastr(
      this.localizationService.translate(
        'families.familyAddSuccessful'
      )
    );
  }

  @Action(FamilyActions.SetFamilyAddDefaultState)
  setFamilyAddDefaultState(ctx: StateContext<FamilyStateModel>,
    {}: FamilyActions.SetFamilyAddDefaultState
  ) {
    ctx.setState(
      patch({
        familyAddForm: patch({
          model: defaults.familyAddForm.model,
        }),
      })
    );
  }

  @Action(FamilyActions.SetFamilyFormDefaultState)
  setFamilyFormDefaultState(ctx: StateContext<FamilyStateModel>, {}: FamilyActions.SetFamilyFormDefaultState) {
    ctx.setState(
      patch({
        familyForm: defaults.familyForm,
        isSubmitted: false
      })
    );
  }

  @Action(FamilyActions.SetFamilyDefaults)
  setFamilysDefaults(
    ctx: StateContext<FamilyStateModel>,
    {}: FamilyActions.SetFamilyDefaults
  ) {
    ctx.patchState(defaults);
  }

  @Action(FamilyActions.SetBankAccount1Form)
  setIbanFamilyForm(
    ctx: StateContext<FamilyStateModel>,
    { bankAccount1 }: FamilyActions.SetBankAccount1Form
  ) {
    ctx.setState(
      patch({
        familyForm: patch({ model: patch({ bankAccount1: bankAccount1 }) }),
      })
    );
  }

  @Action(FamilyActions.SetBankAccount1AddForm)
  setIbanFamilyAddForm(
    ctx: StateContext<FamilyStateModel>,
    { bankAccount1 }: FamilyActions.SetBankAccount1AddForm
  ) {
    ctx.setState(
      patch({
        familyAddForm: patch({ model: patch({ bankAccount1: bankAccount1 }) }),
      })
    );
  }

  @Action(FamilyActions.Error)
  onErrorOcurred(
    ctx: StateContext<FamilyStateModel>,
    { error }: FamilyActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoadingFamilies: false,
    });
    MappingUtilsService.showError(
      this.getTitle(),
      error,
      this.alertService,
      this.localizationService
    );
  }

  private getFamilyRequest(familyForm: FamilyAddForm) {
    let familyRequest = new FamilyRequest();
    familyRequest.familyName = familyForm.familyName;
    familyRequest.firstName = familyForm.firstName;
    familyRequest.lastName = familyForm.lastName;
    familyRequest.iban = familyForm.bankAccount1;
    familyRequest.bank = familyForm.bankAccount2;
    familyRequest.branch = familyForm.bankAccount3;
    familyRequest.controlDigit = familyForm.bankAccount4
      .toString()
      .substring(0, 2);
    familyRequest.internalBankAccountNumber =
      familyForm.bankAccount4.toString().substring(2, 4) +
      familyForm.bankAccount5 +
      familyForm.bankAccount6;
    familyRequest.identification = familyForm.identification;
    familyRequest.isAmpa = familyForm.isAmpa;
    familyRequest.address = familyForm.address;
    familyRequest.city = familyForm.city;
    familyRequest.province = familyForm.province;
    familyRequest.email = familyForm.email;
    familyRequest.zipCode = familyForm.zipCode;
    familyRequest.sendInvoiceByEmail = familyForm.sendInvoiceByEmail;
    familyRequest.phone = familyForm.phone;
    familyRequest.mobilePhone = familyForm.mobilePhone;
    familyRequest.familyDocumentType = familyForm.identificationType;
    familyRequest.languageId = familyForm.languageId;
    familyRequest.exerciseId = familyForm.exerciseId;
    familyRequest.schoolId = familyForm.schoolId;
    familyRequest.profitCenterCode = familyForm.profitCenterCode;
    familyRequest.schoolNumber = familyForm.schoolNumber;
    return familyRequest;
  }

  private createUpdateFamilyData(familyDetailForm: FormGroup, state: FamilyStateModel): UpdateFamilyData {
    let updateFamilyData = new UpdateFamilyData();
    updateFamilyData.familyId = familyDetailForm.get('familyId').value;
    updateFamilyData.familyName = familyDetailForm.get('familyName').value;
    updateFamilyData.firstName = familyDetailForm.get('firstName').value;
    updateFamilyData.lastName = familyDetailForm.get('lastName').value;
    updateFamilyData.iban = familyDetailForm.get('bankAccount1').value;
    updateFamilyData.bank = familyDetailForm.get('bankAccount2').value;
    updateFamilyData.branch = familyDetailForm.get('bankAccount3').value;
    updateFamilyData.controlDigit = familyDetailForm.get('bankAccount4').value.toString().substring(0, 2);
    updateFamilyData.internalBankAccountNumber = familyDetailForm.get('bankAccount4').value.toString().substring(2, 4) + familyDetailForm.get('bankAccount5').value + familyDetailForm.get('bankAccount6').value;
    updateFamilyData.accountingAccount =state.familyStudent.family.accountingAccount;
    updateFamilyData.customerKey = familyDetailForm.get('customerKey').value;
    updateFamilyData.identification = familyDetailForm.get('identification').value;
    updateFamilyData.isAmpa = familyDetailForm.get('isAmpa').value;
    updateFamilyData.address = familyDetailForm.get('address').value;
    updateFamilyData.city = familyDetailForm.get('city').value;
    updateFamilyData.province = familyDetailForm.get('province').value;
    updateFamilyData.email = familyDetailForm.get('email').value;
    updateFamilyData.zipCode = familyDetailForm.get('zipCode').value;
    updateFamilyData.sendInvoiceByEmail = familyDetailForm.get('sendInvoiceByEmail').value;
    updateFamilyData.phone = familyDetailForm.get('phone').value;
    updateFamilyData.mobilePhone = familyDetailForm.get('mobilePhone').value;
    updateFamilyData.debt = familyDetailForm.get('debt').value;
    updateFamilyData.debtStateId = familyDetailForm.get('debtStateId').value;
    updateFamilyData.debtYearId = familyDetailForm.get('debtYearId').value;
    updateFamilyData.familyDocumentType = familyDetailForm.get('familyDocumentType').value;
    updateFamilyData.languageId = familyDetailForm.get('languageId').value;
    updateFamilyData.exerciseId = state.exerciseId;
    updateFamilyData.schoolId = state.familyStudent.family.schoolId;
    updateFamilyData.profitCenterCode =
      state.familyStudent.family.profitCenterCode;
    updateFamilyData.isActive = familyDetailForm.get('isActive').value;
    updateFamilyData.schoolNumber = state.familyStudent.family.schoolNumber;
    let studentsFormArray = familyDetailForm.get('familyStudents') as FormArray<FormGroup>;
    updateFamilyData.students = this.createUpdateFamilyDataStudents(studentsFormArray);
    return updateFamilyData;
  }

  private createUpdateFamilyDataStudents(studentsFormArray: FormArray<FormGroup>): UpdateStudentData[] {
    let updateStudentsData = [];
    if (!studentsFormArray) return updateStudentsData;
    let studentForms : FormGroup[] = studentsFormArray.controls;
    studentForms.filter(x => x.dirty).forEach((studentForm) => {
      let updateStudentData = new UpdateStudentData();
      updateStudentData.studentId = studentForm.get('studentId').value;
      updateStudentData.name = studentForm.get('name').value;
      updateStudentData.surname = studentForm.get('surname').value;
      updateStudentData.lastname = studentForm.get('lastname').value;
      updateStudentData.courseId = studentForm.get('courseId').value;
      updateStudentData.classroom = studentForm.get('classroom').value ? studentForm.get('classroom').value : '';
      updateStudentData.nacionality = studentForm.get('nacionality').value;
      updateStudentData.birthday = studentForm.get('birthday').value !== '' ? studentForm.get('birthday').value : null;
      updateStudentData.gender = studentForm.get('gender').value;
      updateStudentData.transferPayment = studentForm.get('transferPayment').value;
      updateStudentData.cauCode = studentForm.get('cauCode').value;
      updateStudentData.card = studentForm.get('card').value;
      updateStudentData.email = studentForm.get('email').value;
      updateStudentData.guardianIdentification = studentForm.get('guardianIdentification').value;
      updateStudentData.annotations = studentForm.get('annotations').value ? studentForm.get('annotations').value : '';
      updateStudentData.studentDiets = studentForm.get('studentDiets').value;
      let studentEngagementsFormArray = studentForm.get('currentEngagementFamilyStudents') as FormArray<FormGroup>;
      let studentScholarshipsFormArray = studentForm.get('currentScholarshipFamilyStudents') as FormArray<FormGroup>;
      updateStudentData.studentEngagements = this.createUpdateFamilyDataStudentEngagements(studentEngagementsFormArray);
      updateStudentData.studentScholarships = this.createUpdateFamilyDataStudentScholarships(studentScholarshipsFormArray);
      updateStudentsData.push(updateStudentData);

    });
    return updateStudentsData;
  }

  private createUpdateFamilyDataStudentEngagements(studentEngagementFormArray: FormArray<FormGroup>): UpdateStudentEngagementData[] {
    let updateStudentEngagementsData = [];
    if (!studentEngagementFormArray) return updateStudentEngagementsData;
    let studentEngagementForms : FormGroup[] = studentEngagementFormArray.controls;
    studentEngagementForms.filter(x => x.dirty).forEach((studentEngagementForm) => {
      let updateStudentEngagementData = new UpdateStudentEngagementData();
      updateStudentEngagementData.engagementId = studentEngagementForm.get('engagementId').value;
      updateStudentEngagementData.serviceId = studentEngagementForm.get('serviceId').value;
      updateStudentEngagementData.frequencyTypeId = studentEngagementForm.get('frequencyTypeId').value;
      updateStudentEngagementData.startDate = studentEngagementForm.get('startDate').value;
      updateStudentEngagementData.endDate = studentEngagementForm.get('endDate').value;
      updateStudentEngagementData.monday = studentEngagementForm.get('monday').value;
      updateStudentEngagementData.tuesday = studentEngagementForm.get('tuesday').value;
      updateStudentEngagementData.wednesday = studentEngagementForm.get('wednesday').value;
      updateStudentEngagementData.thursday = studentEngagementForm.get('thursday').value;
      updateStudentEngagementData.friday = studentEngagementForm.get('friday').value;
      updateStudentEngagementData.saturday = studentEngagementForm.get('saturday').value;
      updateStudentEngagementData.sunday = studentEngagementForm.get('sunday').value;
      updateStudentEngagementsData.push(updateStudentEngagementData);
    });
    return updateStudentEngagementsData;
  }

  private createUpdateFamilyDataStudentScholarships(studentScholarshipFormArray: FormArray<FormGroup>): UpdateStudentScholarshipData[] {
    let updateStudentScholarshipsData = [];
    if (!studentScholarshipFormArray) return updateStudentScholarshipsData;
    let studentScholarshipForms : FormGroup[] = studentScholarshipFormArray.controls;
    studentScholarshipForms.filter(x => x.dirty).forEach((studentScholarshipForm) => {
      let updateStudentScholarshipData = new UpdateStudentScholarshipData();
      updateStudentScholarshipData.studentScholarshipId = studentScholarshipForm.get('studentScholarshipId').value;
      updateStudentScholarshipData.scholarshipId = studentScholarshipForm.get('scholarshipId').value;
      updateStudentScholarshipData.serviceId = studentScholarshipForm.get('serviceId').value;
      updateStudentScholarshipData.startDate = studentScholarshipForm.get('startDate').value;
      updateStudentScholarshipData.endDate = studentScholarshipForm.get('endDate').value;
      updateStudentScholarshipsData.push(updateStudentScholarshipData);
    });
    return updateStudentScholarshipsData;
  }

  private getFamilyForm(familyStudent: FamilyStudent) {
    let familyForm = new FamilyForm();
    if (
      familyStudent &&
      familyStudent.family &&
      familyStudent.family.bankAccount
    ) {
      let bankAccounts = familyStudent.family.bankAccount.split(' ');
      familyForm.bankAccount1 = bankAccounts[0];
      familyForm.bankAccount2 = bankAccounts[1];
      familyForm.bankAccount3 = bankAccounts[2];
      familyForm.bankAccount4 = bankAccounts[3];
      familyForm.bankAccount5 = bankAccounts[4];
      familyForm.bankAccount6 = bankAccounts[5];
    }
    familyForm.familyId = familyStudent.family.familyId;
    familyForm.familyName = familyStudent.family.familyName;
    familyForm.firstName = familyStudent.family.firstName;
    familyForm.lastName = familyStudent.family.lastName;
    familyForm.customerKey = familyStudent.family.customerKey;
    familyForm.oracleNumber = familyStudent.family.oracleNumber;
    familyForm.identification = familyStudent.family.identification;
    familyForm.customerName = familyStudent.family.customerName;
    familyForm.isAmpa = familyStudent.family.isAmpa;
    familyForm.schoolName = familyStudent.family.schoolName;
    familyForm.address = familyStudent.family.address;
    familyForm.city = familyStudent.family.city;
    familyForm.province = familyStudent.family.province;
    familyForm.email = familyStudent.family.email;
    familyForm.zipCode = familyStudent.family.zipCode;
    familyForm.sendInvoiceByEmail = familyStudent.family.sendInvoiceByEmail;
    familyForm.phone = familyStudent.family.phone;
    familyForm.mobilePhone = familyStudent.family.mobilePhone;
    familyForm.debt = familyStudent.family.debt;
    familyForm.debtStateId = familyStudent.family.debtStateId;
    familyForm.debtYearId = familyStudent.family.debtYearId;
    familyForm.languageId = familyStudent.family.languageId;
    familyForm.isActive = familyStudent.family.isActive;
    familyForm.familyStudents = familyStudent.familyStudents;
    familyForm.familyDocumentType = familyStudent.family.familyDocumentType;
    familyForm.schoolId = familyStudent.family.schoolId;
    return familyForm;
  }

  private callGetFamilies(
    ctx: StateContext<FamilyStateModel>,
    filters: FamilyFilter
  ) {
    ctx.patchState({ isLoadingFamilies: true });
    const user = this.store.selectSnapshot(AuthSelectors.getUser);
    return this.familyService.getFamiliesByFilter(user, filters).subscribe({
      next: (response) =>
        ctx.dispatch(new FamilyActions.GetFamiliesSuccess(response)),
      error: (error) => ctx.dispatch(new FamilyActions.Error(error)),
    });
  }

  private showToastr(message) {
    this.toastr.success(message, this.getTitle());
  }

  private getTitle() {
    return this.localizationService.translate('labels.family');
  }
}
